import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { format } from 'date-fns';

import { ARServiceItemList } from '@/models/airRoad';
import { brandConfig } from '@/brand';

export const jobCodesData = {
  STAIRS: {
    id: 1,
    description: 'Stairs needed',
    icon: 'mdi-stairs',
  },
  TECH1: {
    id: 2,
    description: 'Technicial lvl 1',
    icon: 'mdi-progress-wrench',
  },
  TECH2: {
    id: 3,
    description: 'Technicial lvl 2',
    icon: 'mdi-progress-wrench',
  },
  TECH3: {
    id: 4,
    description: 'Technicial lvl 3',
    icon: 'mdi-progress-wrench',
  },
  TECH: {
    id: 5,
    description: 'Technician required',
    icon: 'mdi-wrench',
  },
  LSC: {
    id: 6,
    description: 'Large Stair-climber',
    icon: 'mdi-stairs',
  },
  SSC: {
    id: 7,
    description: 'Small Stair-climber',
    icon: 'mdi-stairs',
  },
  PPWK: {
    id: 8,
    description: 'Paperwork required',
    icon: 'mdi-file-document',
  },
  ONE: {
    id: 9,
    description: 'One-man job',
    icon: 'mdi-account',
  },
  FLR: {
    id: 10,
    description: 'Extra Items to Load',
    icon: 'mdi-caravan',
  },
  LIFT: {
    id: 11,
    description: 'Lifting gear/rigger',
    icon: 'mdi-forklift',
  },
  EXTR: {
    id: 12,
    description: 'Extra men required',
    icon: 'mdi-account-plus',
  },
  LOW: {
    id: 13,
    description: 'Low Dock Height',
    icon: 'mdi-format-align-bottom',
  },
  LATE: {
    id: 14,
    description: 'Late Booking',
    icon: 'mdi-clock-alert',
  },
  DIFF: {
    id: 15,
    description: 'Difficult job',
    icon: 'mdi-school',
  },
  PPE: {
    id: 16,
    description: 'Safety Equipment Required',
    icon: 'mdi-face-mask',
  },
};

export const AU_PHONE_PREFIX = '+61';

// Mirror the API contract directly so this type can never silently drift from it
export type InstallationValue =
  openapi.PostJobBookApi_Body[number]['products'][number]['installation'];

export const installationOptions: readonly {
  text: string;
  value: InstallationValue;
}[] = [
  { text: 'Unpack, position and test', value: 'DLVPOS' },
  { text: 'Deliver only', value: 'DLVONLY' },
  { text: 'Basic TECH1', value: 'TECH1' },
  { text: 'Standard TECH2', value: 'TECH2' },
  { text: 'Full TECH3', value: 'TECH3' },
];

export const installationOptionsText: Partial<
  Record<InstallationValue, string>
> = {
  DLVPOS: 'Plugin and test if applicable',
  DLVONLY: 'Delivery only. Will be left packed',
  TECH1: `Set IP, call IT to configure and confirm the device is connected to the network.
    20mins allocated to installation.`,
  TECH2: `Set up main functions and train end-user. Configuration details required to complete set-up.
    40mins allocated to installation.`,
  TECH3: `Set up main functions and train end-user. High-level installation where no configuration details are
    available. 80mins allocated to installation.`,
};

export type ReturnValue = 'STORE' | 'RECYCLE' | 'RETURN' | 'WORKSHOP' | '';

export const returnOptionsList: readonly ReturnValue[] = [
  'STORE',
  'RECYCLE',
  'RETURN',
  'WORKSHOP',
];
export const returnOptions: readonly { text: string; value: ReturnValue }[] = [
  { text: 'Stored', value: 'STORE' },
  { text: 'Recycled', value: 'RECYCLE' },
  { text: 'Forward to', value: 'RETURN' },
  { text: 'Go to Workshop', value: 'WORKSHOP' },
];

export const returnOptionsTexts: Partial<Record<ReturnValue, string>> = {
  STORE:
    'Items will be placed in storage if scrap, return or workshop is not selected',
  RECYCLE: 'Items will be sent for recycling',
  RETURN:
    'For non-urgent deliveries back to your warehouse or an alternative location. ' +
    'Please add final address in delivery instructions',
  WORKSHOP: 'Items will be moved to KK workshop for runup/refurb',
};

// NOTE: These are placeholder values for demo purposes.
// Real values are not yet known and backend does not currently accept them.
export const PREDEFINED_WORKSHOP_TASK_OPTIONS: {
  value: string;
  title: string;
}[] = [
  { value: 'INSPECT', title: 'Inspect' },
  { value: 'CLEAN', title: 'Clean' },
  { value: 'REPAIR', title: 'Repair' },
  { value: 'REFURBISH', title: 'Refurbish' },
];

export const notificationErrorText =
  'Call the helpful team for assistance on 02 9575 0000 or email to kkts_it@kkts.com.au';

export const arJobStatuses = [
  { text: 'Not booked', value: 0 },
  { text: 'Booked', value: 1 },
  { text: 'In transit', value: 2 },
  { text: 'PoD received', value: 3 },
  { text: 'Failed to book', value: 1001 },
  { text: 'Queued to book', value: 1002 },
];

export const statesTimeZonesList = {
  WA: {
    timezone: 'Australia/Perth',
    code: 'AWST',
  },
  NT: {
    timezone: 'Australia/Darwin',
    code: 'NT',
  },
  SA: {
    timezone: 'Australia/Adelaide',
    code: 'ACDT',
  },
  QLD: {
    timezone: 'Australia/Brisbane',
    code: 'AEST',
  },
  TAS: {
    timezone: 'Australia/Tasmania',
    code: 'AET',
  },
  VIC: {
    timezone: 'Australia/Victoria',
    code: 'AEDT',
  },
  ACT: {
    timezone: 'Australia/Victoria',
    code: 'ACT',
  },
  NZ: {
    timezone: 'Pacific/Auckland',
    code: 'NZDT',
  },
  NSW: {
    timezone: 'Australia/Sydney',
    code: 'AEDT',
  },
};

export const timeZoneDefault = 'Australia/Sydney';

export const warehouseStatusList = {
  D: 'Despatch',
  I: 'Incoming',
  S: 'Stock',
  T: 'Transit',
  X: 'Unknown',
};

export const zammadTicketRfcStatuses = [
  'NEW',
  'OPEN',
  'PENDING_REMINDER',
  'PENDING_CLOSE',
  'AWAITING_DIMS',
  'AWAITING_CUSTOMER',
];

export const stateNames = [
  'Sydney',
  'Brisbane',
  'Adelaide',
  'Melbourne',
  'Perth',
  'Darwin',
  'Canberra',
];

// TODO: REFACTOR TO USE FROM CATALOG API?
//       OR TO CREATE AN OBJECT
// THIS IS TO USE AS HARDCODED DIVISIONS
export const kktsDivisions = [
  'SYDNEY',
  'BRISBANE',
  'ADELAIDE',
  'MELBOURNE',
  'PERTH',
  'DARWIN',
  'CANBERRA',
];

// THIS IS FOR SPLIT TO BASE FUNCTION
export const kktsSplitToBase = [
  'SYDNEY',
  'BRISBANE',
  'MELBOURNE',
  'ADELAIDE',
  'PERTH',
  'CANBERRA',
  'DARWIN',
];

export const logoTitle: string = brandConfig.logoTitle;

export const stateShortNames = [
  'WA',
  'NT',
  'SA',
  'QLD',
  'TAS',
  'VIC',
  'ACT',
  'NZ',
  'NSW',
].sort();

/* eslint-disable camelcase */
const ARServiceItem_TL = {
  code: 'TL',
  name: 'TailLift Required',
  specialOptionId: 1,
  chosen: false,
};

const ARServiceItem_2M = {
  code: '2M',
  name: '2 Men Required',
  specialOptionId: 19,
  chosen: false,
};

const ARServiceItem_AMSrv = {
  code: 'AMSrv',
  name: 'AM Service',
  specialOptionId: 23,
  chosen: false,
};

const ARServiceItem_PMSrv = {
  code: 'PMSrv',
  name: 'PM Service',
  specialOptionId: 24,
  chosen: false,
};

const ARServiceItem_PckReq = {
  code: 'PckReq',
  name: 'Packaging Required',
  stopAvailable: 1,
  specialOptionId: 26,
  chosen: false,
  description: '',
};

const ARServiceItem_HndLd = {
  code: 'HndLd',
  name: 'Hand Loading',
  specialOptionId: 37,
  chosen: false,
};

const ARServiceItem_HndUnLd = {
  code: 'HndUnLd',
  name: 'Hand Unloading',
  specialOptionId: 38,
  chosen: false,
};

const ARServiceItem_SatSrv = {
  code: 'SatSrv',
  name: 'Saturday Service',
  specialOptionId: 27,
  chosen: false,
  contact_name: '',
  contact_phone: '',
};

// TODO refactor
const ARServiceItem_PckUpTime = {
  code: 'PckUpTime',
  name: 'Specific Pickup Time',
  stopAvailable: 1,
  specialOptionId: 29,
  chosen: false,
  contact_name: '',
  contact_phone: '',
  date: new Date(),
  time: '12:00 PM',
  _date: format(new Date(), 'yyyy-MM-dd'),
  _time: {
    hours: 12,
    minutes: 0,
    seconds: 0,
  },
};

// TODO refactor
const ARServiceItem_DelTime = {
  code: 'DelTime',
  name: 'Specific Delivery Time',
  stopAvailable: 2,
  specialOptionId: 30,
  chosen: false,
  contact_name: '',
  contact_phone: '',
  date: new Date(),
  time: '12:00 PM',
  _date: format(new Date(), 'yyyy-MM-dd'),
  _time: {
    hours: 12,
    minutes: 0,
    seconds: 0,
  },
};

const ARServiceItem_Unpack = {
  code: 'Unpack',
  name: 'Unpack & remove Rubbish',
  specialOptionId: 31,
  chosen: false,
  description: '',
};

// TODO refactor
const ARServiceItem_DtRange = {
  code: 'DtRange',
  name: 'Specific Date Range',
  specialOptionId: 35,
  chosen: false,
  contact_name: '',
  contact_phone: '',
  dateFrom: new Date(),
  dateTo: new Date(),
  _dateFrom: format(new Date(), 'yyyy-MM-dd'),
  _dateTo: format(new Date(), 'yyyy-MM-dd'),
};

const ARServiceItem_DVhcl = {
  code: 'DVhcl',
  name: 'Dedicated Vehicle',
  specialOptionId: 36,
  chosen: false,
};

export const ARServicesDeliveryList: ARServiceItemList = {
  TL: { ...ARServiceItem_TL },
  '2M': { ...ARServiceItem_2M },
  AMSrv: { ...ARServiceItem_AMSrv },
  PMSrv: { ...ARServiceItem_PMSrv },
  SatSrv: { ...ARServiceItem_SatSrv },
  DelTime: { ...ARServiceItem_DelTime },
  Unpack: { ...ARServiceItem_Unpack },
  DtRange: { ...ARServiceItem_DtRange },
  DVhcl: { ...ARServiceItem_DVhcl },
  HndUnLd: { ...ARServiceItem_HndUnLd },
};
export const ARServicesPickupList: ARServiceItemList = {
  TL: { ...ARServiceItem_TL },
  '2M': { ...ARServiceItem_2M },
  AMSrv: { ...ARServiceItem_AMSrv },
  PMSrv: { ...ARServiceItem_PMSrv },
  PckReq: { ...ARServiceItem_PckReq },
  SatSrv: { ...ARServiceItem_SatSrv },
  PckUpTime: { ...ARServiceItem_PckUpTime },
  Unpack: { ...ARServiceItem_Unpack },
  DtRange: { ...ARServiceItem_DtRange },
  DVhcl: { ...ARServiceItem_DVhcl },
  HndLd: { ...ARServiceItem_HndLd },
};

export const JOB_EDIT_LOCK_TIMEOUT_DEV = 100 * 1000;
export const JOB_EDIT_LOCK_COUNTDOWN_DURATION_DEV = 20;
export const JOB_EDIT_LOCK_TIMEOUT_PROD = 8 * 60 * 1000;
export const JOB_EDIT_LOCK_COUNTDOWN_DURATION_PROD = 2 * 60;
/* eslint-enable camelcase */

export const BOOKING_AM_WINDOW = {
  startMin: 9 * 60,
  endMin: 12 * 60,
} as const;

export const BOOKING_PM_WINDOW = {
  startMin: 12 * 60,
  endMin: 17 * 60,
} as const;

export const BOOKING_DAY_START_MIN = 9 * 60;
export const BOOKING_DAY_END_MIN = 17 * 60;
export const BOOKING_WAREHOUSE_DAY_START_MIN = 7 * 60;
